// Layer 3 (Transformation) — the panel row's right column (polish.P20, locked
// comp `risk.html` A): what waits on the GM. Two decisions only the Plant
// Manager seat takes:
//
//   1. The OCR-01 override-authority gate (adjudication 3) — screen-local, like
//      the Advisory gate: Approve and Decline for the GM, read-only for every
//      other persona. The interlock row's authority does not change.
//   2. The BM01 stop (adjudication 4) — one compact row read from the case's
//      `stopPlan` (`stopDecision`, window, work order), linking to the case.
//
// Pure: no React; the persona, the clock and the gate decision arrive as
// arguments.

import type { Persona } from '@/shared/types';
import { CASES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { approvedPillText } from '../advisory/AgentInvestigation.transform';
import { OVERRIDE_CHANGE_REQUESTS, RISK_ENTRIES, SAFETY_INTERLOCKS } from './seed';
import { formatShiftDate } from './Scene.transform';

/** The persona that holds the Plant Manager seat (`person-gm`). */
export const PLANT_MANAGER_PERSONA: Persona = 'GM';

export type GateDecision = { kind: 'approved' | 'declined'; at: string };

export type OverrideGateRead = {
  requestId: string;
  interlockId: string;
  /** `BM01 Motor · motor power HH > 1,200 kW · trip feed CV02`. */
  subject: string;
  fromAuthority: string;
  toAuthority: string;
  reason: string;
  requestedBy: string;
  requestedOn: string;
  approver: string;
  status: 'pending' | 'approved' | 'declined';
  pill: string;
  canDecide: boolean;
};

export type StopRowRead = {
  caseId: string;
  caseCode: string;
  title: string;
  due: string;
  decides: string;
  confirms: string;
  refs: string;
};

export type WaitingRead = {
  title: string;
  count: number;
  gate: OverrideGateRead | null;
  stop: StopRowRead | null;
};

/** Lower-case a clause's first letter unless it opens on an acronym (`HP lube…`). */
function clause(text: string): string {
  const [first, second] = text;
  if (!first || (second && second === second.toUpperCase() && /[A-Z]/.test(second))) return text;
  return first.toLowerCase() + text.slice(1);
}

function capitalise(text: string): string {
  return text ? text[0]!.toUpperCase() + text.slice(1) : text;
}

function gateRead(persona: Persona, decision: GateDecision | null): OverrideGateRead | null {
  const request = OVERRIDE_CHANGE_REQUESTS.find((row) => row.status === 'Pending approval');
  if (!request) return null;
  const interlock = SAFETY_INTERLOCKS.find((row) => row.id === request.interlockId);
  const subject = interlock
    ? `${interlock.equipment} · ${clause(interlock.condition)} · ${clause(interlock.effect)}`
    : request.interlockId;
  const status = decision?.kind ?? 'pending';
  const pill = decision
    ? decision.kind === 'approved'
      ? approvedPillText(decision.at, request.approver)
      : `Declined · ${decision.at.slice(11, 16)} · ${request.approver}`
    : request.status;
  return {
    requestId: request.id,
    interlockId: request.interlockId,
    subject,
    fromAuthority: request.fromAuthority,
    toAuthority: request.toAuthority,
    reason: request.reason.endsWith('.') ? request.reason : `${request.reason}.`,
    requestedBy: request.requestedBy,
    requestedOn: formatShiftDate(request.requestedOn),
    approver: request.approver,
    status,
    pill,
    canDecide: persona === PLANT_MANAGER_PERSONA && status === 'pending',
  };
}

function stopRead(): StopRowRead | null {
  const found = CASES.find((item) => item.stopPlan?.stopDecision?.decides === 'GM');
  const plan = found?.stopPlan;
  const decision = plan?.stopDecision;
  if (!found || !plan || !decision) return null;
  const risk = RISK_ENTRIES.find((row) => row.linkedWorkOrder === plan.workOrderId);
  const [asset, part] = (plan.workOrder?.asset ?? '').split(' · ');
  const hours = risk?.plannedStopHours;
  const what = hours !== undefined ? `the ${hours} h ${part ?? ''} inspection`.replace(/\s+/g, ' ') : `${part ?? ''} inspection`;
  return {
    caseId: found.id,
    caseCode: found.code ?? found.id,
    title: `Stop ${asset || 'the asset'} for ${what} · ${plan.window.label}`,
    due: capitalise(decision.due),
    decides: decision.decides,
    confirms: decision.confirms,
    refs: [risk?.id, plan.workOrderId].filter(Boolean).join(' · '),
  };
}

export function waitingOnYou(persona: Persona, decision: GateDecision | null): WaitingRead {
  const gate = gateRead(persona, decision);
  const stop = stopRead();
  const count = (gate?.status === 'pending' ? 1 : 0) + (stop ? 1 : 0);
  return {
    title: persona === PLANT_MANAGER_PERSONA ? 'Waiting on you' : 'Waiting on the GM',
    count,
    gate,
    stop,
  };
}
